import { useEffect } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import type { ViewshedResult } from "./computeViewshed";

const SOURCE = "tactical-viewshed";
const FILL = "tactical-viewshed-fill";
const LINE = "tactical-viewshed-line";
const POINT = "tactical-viewshed-observer";

type Props = {
  map: MapLibreMap | null;
  result: ViewshedResult | null;
};

function toCollection(result: ViewshedResult | null): GeoJSON.FeatureCollection {
  if (!result || result.ring.length < 4) {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: { role: "zone", flat: result.flatEarth ? 1 : 0 },
        geometry: { type: "Polygon", coordinates: [result.ring] },
      },
      {
        type: "Feature",
        properties: { role: "observer" },
        geometry: {
          type: "Point",
          coordinates: [result.observer.lng, result.observer.lat],
        },
      },
    ],
  };
}

/** Overlay зоны viewshed. Не участвует в CAD-выборе. */
export function TacticalViewshedOverlay({ map, result }: Props) {
  useEffect(() => {
    if (!map) return;

    const ensure = () => {
      if (!map.getSource(SOURCE)) {
        map.addSource(SOURCE, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
      }
      if (!map.getLayer(FILL)) {
        map.addLayer({
          id: FILL,
          type: "fill",
          source: SOURCE,
          filter: ["==", ["get", "role"], "zone"],
          paint: {
            "fill-color": [
              "case",
              ["==", ["get", "flat"], 1],
              "#94a3b8",
              "#22c55e",
            ],
            "fill-opacity": 0.22,
          },
        });
      }
      if (!map.getLayer(LINE)) {
        map.addLayer({
          id: LINE,
          type: "line",
          source: SOURCE,
          filter: ["==", ["get", "role"], "zone"],
          paint: {
            "line-color": [
              "case",
              ["==", ["get", "flat"], 1],
              "#64748b",
              "#15803d",
            ],
            "line-width": 2,
            "line-opacity": 0.9,
          },
        });
      }
      if (!map.getLayer(POINT)) {
        map.addLayer({
          id: POINT,
          type: "circle",
          source: SOURCE,
          filter: ["==", ["get", "role"], "observer"],
          paint: {
            "circle-radius": 6,
            "circle-color": "#1d4ed8",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#fff",
          },
        });
      }
    };

    const sync = () => {
      try {
        ensure();
        const src = map.getSource(SOURCE) as
          | { setData: (d: GeoJSON.FeatureCollection) => void }
          | undefined;
        src?.setData(toCollection(result));
      } catch {
        /* style */
      }
    };

    if (map.isStyleLoaded()) sync();
    else map.once("load", sync);
    map.on("styledata", sync);
    sync();

    return () => {
      map.off("styledata", sync);
      try {
        if (map.getLayer(POINT)) map.removeLayer(POINT);
        if (map.getLayer(LINE)) map.removeLayer(LINE);
        if (map.getLayer(FILL)) map.removeLayer(FILL);
        if (map.getSource(SOURCE)) map.removeSource(SOURCE);
      } catch {
        /* disposed */
      }
    };
  }, [map, result]);

  return null;
}
